import React, { useState } from 'react';
import { Select, NumberInput, Text, Stack } from '@mantine/core';
import { Formik, Form } from 'formik';
import {Item, itemStore} from '../../../../../../redux/entities/inventory/item';
import { transferInventory } from '../../../../../../api/inventory/inventoryApi';
import {ETransferFrom, Transfer, transferStore} from "../../../../../../redux/entities/inventory/transfer";
import {bindActionCreators, Dispatch} from 'redux';
import {CommonState} from '../../../../../../redux';
import {connect} from "react-redux";
import StandardModal from '../../../../../../components/util/StandardModal/StandardModal';

type Props = {
  opened: boolean;
  onClose: () => void;
  inventory: Item[];
  onSuccess: (updated: Item) => void;
} & ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatchToProps>;


const TransferModal = (props: Props) => {
  const { opened, onClose, inventory, onSuccess, transfers } = props;
  const [isTransferring, setIsTransferring] = useState(false);
  const [transferError, setTransferError] = useState<string | undefined>();

  const formId = 'transfer-form';
  return (
    <StandardModal
      opened={opened}
      onCancel={onClose}
      title={"Transfer Inventory"}
      submitText={isTransferring ? 'Transferring...' : 'Submit'}
      cancelText={'Cancel'}
      submitDisabled={isTransferring}
      loading={isTransferring}
      formId={formId}
      noBodyScroll={true}
      // Prevent auto-focus on first input so the Item Select doesn't auto-open on modal mount
      trapFocus={false}
      initialFocus={-1 as any}
    >
      <Formik<Transfer>
        initialValues={{id: '', itemId: inventory[0]?.id ?? '', transferFrom: ETransferFrom.StoreToWarehouse, quantity: 1 }}
        onSubmit={async (vals, { setSubmitting }) => {
          setTransferError(undefined);
          setIsTransferring(true);
          try {
            // Ensure quantity is a positive integer
            const qty = Math.max(1, Math.floor(vals.quantity ?? 1));
            const payload: Transfer = { ...vals, quantity: qty } as any;
            const updated = await transferInventory(payload);
            onSuccess(updated);
            onClose();
          } catch (e: any) {
            setTransferError(e?.message ?? 'Failed to transfer inventory');
          }
          setIsTransferring(false);
          setSubmitting(false);
        }}
      >
        {({ values, setFieldValue, handleSubmit }) => (
          <Form onSubmit={handleSubmit} id={formId}>
            <Stack spacing="sm">
              <Select
                label="Item"
                placeholder="Select item"
                data={(inventory ?? []).map(i => ({ value: i.id, label: i.name }))}
                value={values.itemId}
                onChange={(v) => setFieldValue('itemId', v ?? '')}
                searchable
                nothingFound="No items"
                withinPortal={true}
              />
              <Select
                label="Moving from"
                // Mantine Select expects string values. Map enum (number) <-> string.
                data={[
                  { value: String(ETransferFrom.StoreToWarehouse), label: 'Store to warehouse' },
                  { value: String(ETransferFrom.WarehouseToStore), label: 'Warehouse to store' },
                ]}
                value={String(values.transferFrom)}
                onChange={(v) => {
                  // Default to StoreToWarehouse if undefined/null
                  const next = v !== null && v !== undefined && v !== '' ? parseInt(v, 10) : ETransferFrom.StoreToWarehouse;
                  setFieldValue('transferFrom', next);
                }}
                withinPortal={true}
              />
              <NumberInput
                label="Quantity"
                min={1}
                step={1}
                value={values.quantity}
                onChange={(v) => setFieldValue('quantity', typeof v === 'number' ? v : 1)}
              />
              {transferError && (
                <Text color="red" size="sm">{transferError}</Text>
              )}
              {/* Buttons provided by StandardModal */}
            </Stack>
          </Form>
        )}
      </Formik>
    </StandardModal>
  );
};

const mapDispatchToProps = (dispatch: Dispatch) => ({actions: bindActionCreators({}, dispatch)});
const mapStateToProps = (state: CommonState) => ({
  transfers: transferStore.selectors.getAsArray(state)
});

export default connect(mapStateToProps, mapDispatchToProps)(TransferModal);
